import React, { useState, useEffect, SyntheticEvent } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';

import { format } from 'date-fns';
import Grid from '@mui/material/Grid';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableCell from '@mui/material/TableCell';
import Paper from '@mui/material/Paper';
import TableRow from '@mui/material/TableRow';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Snackbar, { SnackbarCloseReason } from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';

import { CircularProgressComponent } from '../../../components/admin/circular-progress.component';
import { CompanyConfig } from '../../../company.config';
import { onError } from '../../../utils/error.util';
import { useAppContext } from '../../../utils/content.util';
import { deletePayment, getPayments } from '../../../services/payments.service';
import { Payment } from '../../../models/payment.model';
import { formatMoney, formatPhoneNumber } from '../../../utils/format.util';
import SearchBox from '../../../components/admin/search-box/search-box.component';
import { MenuItem } from '@mui/material';
import { InputLabel } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import { Select } from '@mui/material';

const LIMIT = 25;
const PAYMENT_STATUSES = ['pending', 'paid', 'failed', 'expired'];

function PaymentTable({
  data,
  page,
  rowsPerPage = LIMIT,
  handleShowDeleteModal,
  handleChangeRowsPerPage,
  handleChangePage,
  copyLink,
}: any) {
  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell> </TableCell>
              <TableCell> </TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Analyst</TableCell>
              <TableCell>Invoice #</TableCell>
              <TableCell> </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row: any) => (
              <PaymentTableRow
                key={row.id}
                row={row}
                copyLink={copyLink}
                handleShowDeleteModal={handleShowDeleteModal}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

function PaymentTableRow({
  row,
  copyLink,
  handleShowDeleteModal,
}: {
  row: Payment;
  copyLink: (itemId: string) => Promise<void>;
  handleShowDeleteModal: (itemId: string) => void;
}) {
  return (
    <TableRow>
      <TableCell>
        <Tooltip title="View">
          <ReactRouterLink key={row.id} to={`/payments/${row.id}`}>
            <VisibilityIcon color="primary" fontSize="small" />
          </ReactRouterLink>
        </Tooltip>
      </TableCell>
      <TableCell align="left">
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Copy Link">
            <ContentCopyIcon
              color="primary"
              fontSize="small"
              onClick={() => copyLink(row.id)}
              sx={{ cursor: 'pointer' }}
            />
          </Tooltip>
        </Box>
      </TableCell>
      <TableCell>
        {row.firstName} {row.lastName}
      </TableCell>
      <TableCell>{row.email}</TableCell>
      <TableCell>{formatPhoneNumber(row.phone)}</TableCell>
      <TableCell align="right">{formatMoney(row.price)}</TableCell>
      <TableCell>{format(row.dueDate!, 'MM-dd-yyyy')}</TableCell>
      <TableCell>{row.status}</TableCell>
      <TableCell>{row.analystName ?? ''}</TableCell>
      <TableCell>{row.invoiceNumber}</TableCell>
      <TableCell align="left">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {row.status === 'pending' && (
            <Tooltip title="Delete">
              <ReactRouterLink onClick={() => handleShowDeleteModal(row.id)} to="">
                <DeleteForeverIcon color="error" fontSize="small" />
              </ReactRouterLink>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
}

export default function PaymentsContainer() {
  const { isAuthenticated } = useAppContext();

  const [paymentItems, setPaymentItems] = useState<any[]>([]);

  const [openAlertCopy, setOpenAlertCopy] = React.useState(false);
  const [lastKey, setLastKey] = useState<string>('');
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const [currentCursor, setCurrentCursor] = useState<string>('');

  const [currentItemId, setCurrentItemId] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isLoading, setIsLoading] = useState(true);

  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [currentName, setCurrentName] = useState<string>('');
  const [shouldReload, setShouldReload] = useState(false);

  const copyLink = async (itemId?: string) => {
    try {
      const itemLink = `${CompanyConfig.paymentUrl}/${itemId}`;
      await navigator.clipboard.writeText(itemLink);
      setOpenAlertCopy(true);
    } catch (err) {
      onError(err);
    }
  };

  const handleClickOpenDelete = (itemId: string) => {
    setCurrentItemId(itemId);
    setShowDeleteModal(true);
  };

  const handleCloseDelete = () => setShowDeleteModal(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deletePayment(currentItemId);
    } catch (err) {
      alert(err);
    } finally {
      setShowDeleteModal(false);
      setIsDeleting(false);
      onLoad();
    }
  };

  const handleCloseAlertCopy = (event: Event | SyntheticEvent<any, Event>, reason: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenAlertCopy(false);
  };

  async function onLoad(name = '', cursor = '') {
    if (!isAuthenticated) {
      return;
    }

    setPaymentItems([]);
    setIsLoading(true);

    try {
      const response = await getPayments({ lastKey: cursor, limit: LIMIT, name, status: selectedStatus });

      const sortedItems = response.items.sort((a: any, b: any) => {
        // If both are pending or both are not pending, sort by due date
        if ((a.status === 'pending' && b.status === 'pending') || (a.status !== 'pending' && b.status !== 'pending')) {
          return new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime();
        }
        // Show pending payments first
        if (a.status === 'pending') return -1;
        if (b.status === 'pending') return 1;
        return 0;
      });
      setPaymentItems(sortedItems);
      setLastKey(response.lastKey);
    } catch (e) {
      onError(e);
    }

    setIsLoading(false);
  }

  useEffect(() => {
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shouldReload) {
      onLoad();
      setShouldReload(false); // Reset the flag
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldReload]);

  const handleSearch = (search: string) => {
    setCurrentName(search);
    setCursorHistory([]);
    setCurrentCursor('');
    onLoad(search);
  };

  const handleStatusChange = (event: any) => {
    setSelectedStatus(event.target.value);
    // onLoad(); // Reload with new status filter
  };

  const handleSearchClear = () => {
    setSelectedStatus('');
    setShouldReload(true); // Set the flag to true
  };

  function renderPaymentItems() {
    return (
      <>
        <div className="dealers">
          <h2 className="pb-2 mt-4 mb-3 border-bottom">Payments</h2>
        </div>
        <div>
          <div className="pb-2 mt-4 mb-3">
            <Grid container spacing={2}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 4 }}>
                  <ReactRouterLink to={'/payments/new'}>
                    <Button variant="contained">New Payment</Button>
                  </ReactRouterLink>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <FormControl fullWidth sx={{ minWidth: 200 }}>
                    <InputLabel id="status-select-label">Status</InputLabel>
                    <Select
                      labelId="status-select-label"
                      id="status-select"
                      value={selectedStatus}
                      onChange={handleStatusChange}
                      label="Status"
                    >
                      <MenuItem value="" sx={{ minWidth: 150 }}>
                        All Statuses
                      </MenuItem>
                      {PAYMENT_STATUSES.map((status) => (
                        <MenuItem key={status} value={status} sx={{ minWidth: 150 }}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 4 }}>
                  <SearchBox
                    placeholder="Name starts with"
                    handleSubmit={handleSearch}
                    handleClearAll={handleSearchClear}
                  />
                </Grid>
              </Grid>
            </Grid>
          </div>
        </div>
      </>
    );
  }

  const handleNext = () => {
    if (lastKey) {
      setCursorHistory([...cursorHistory, currentCursor]);
      setCurrentCursor(lastKey);
      onLoad(currentName, lastKey);
    }
  };

  const handlePrev = () => {
    if (cursorHistory.length > 0) {
      const newHistory = [...cursorHistory];
      const previousCursor = newHistory.pop() || '';
      setCursorHistory(newHistory);
      setCurrentCursor(previousCursor);
      onLoad(currentName, previousCursor);
    }
  };

  return (
    <React.Fragment>
      <Grid size={{ xs: 12 }}>
        <div className="PaymentItems">
          {renderPaymentItems()}
          {isLoading ? (
            <CircularProgressComponent />
          ) : (
            <PaymentTable
              data={paymentItems}
              page={0}
              copyLink={copyLink}
              rowsPerPage={LIMIT}
              lastKey={lastKey}
              handleShowDeleteModal={handleClickOpenDelete}
            />
          )}
          {!isLoading && paymentItems.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 10 }}>
              <Button onClick={handlePrev} disabled={cursorHistory.length === 0}>
                Previous
              </Button>
              <Button onClick={handleNext} disabled={!lastKey}>
                Next
              </Button>
            </Box>
          )}
        </div>
      </Grid>

      <Snackbar open={openAlertCopy} autoHideDuration={6000} onClose={handleCloseAlertCopy}>
        <Alert severity="success" sx={{ width: '100%' }}>
          Copied to clipboard!
        </Alert>
      </Snackbar>

      <Dialog
        open={showDeleteModal}
        onClose={(_event, reason) => {
          if (reason === 'escapeKeyDown') return;
          handleCloseDelete();
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">Are you sure you want to delete this?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDelete} disabled={isDeleting}>
            Delete
          </Button>
          <Button onClick={handleCloseDelete} disabled={isDeleting} autoFocus>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
